import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Paper, Button } from '@mui/material';
import { API_CONFIG, getNodeApiHeaders } from '../helpers/constants';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
  email_id?: string;
}

const GoogleOAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenData, setTokenData] = useState<TokenResponse | null>(null);

  // Derive isAppsScriptFlow from URL parameters or session storage immediately
  const isAppsScriptFlow = useMemo(() => {
    const state = searchParams.get('state');
    const storageUri = sessionStorage.getItem('apps_script_redirect_uri');
    return (state && state.startsWith('apps_script_flow')) || !!storageUri;
  }, [searchParams]);

  // Google Chat Inbox temporarily reuses this callback route — see the longer comment in the
  // effect below. Lifted to component level (like isAppsScriptFlow above) so the loading/success
  // copy can reflect it too, not just the postMessage payload.
  const isGoogleChatInboxFlow = useMemo(() => {
    return searchParams.get('state') === 'google_chat_inbox_oauth';
  }, [searchParams]);

  // Email 401-reauth flow (revoked refresh token) also reuses this callback route — see
  // docs/EmailReauth_401_Recovery_FE_Implementation.md §4.3. State carries which email address
  // is being reauthorized, base64url-encoded, mirroring the apps_script_flow|<base64> pattern.
  const emailReauthAddress = useMemo(() => {
    const state = searchParams.get('state');
    if (state && state.startsWith('email_reauth|')) {
      try {
        const b64 = state.split('|')[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
        return atob(padded);
      } catch {
        return null;
      }
    }
    return null;
  }, [searchParams]);

  // Ensure we only notify parent once (avoids duplicate toasts when Strict Mode double-invokes effect)
  const cancelledNotifySentRef = useRef(false);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        console.log('isAppsScriptFlow', isAppsScriptFlow);
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const state = searchParams.get('state');

        // Google Chat Inbox (DM-only provider) reuses this callback route instead of its own
        // dedicated one (GoogleChatInboxOAuthCallback.tsx / /auth/google-chat-inbox/callback),
        // TEMPORARILY, because that dedicated redirect_uri isn't yet registered in Google Cloud
        // Console for this OAuth client (redirect_uri_mismatch) — this route already is. Tagged
        // via `state` rather than a new path, mirroring the existing apps_script_flow pattern
        // below (isGoogleChatInboxFlow computed at component level, above). Revert to the
        // dedicated route once Console registration lands — see
        // docs/GoogleChatInbox_DM_FE_Implementation.md §5.6.

        // Check if this is an Apps Script flow from the state parameter
        let appsScriptRedirectUri = sessionStorage.getItem('apps_script_redirect_uri');
        console.log('appsScriptRedirectUri', appsScriptRedirectUri);
        if (isAppsScriptFlow) {
          console.log('Apps Script flow detected');
          // If the state contains the redirect URI (piped after current state), extract it
          if (state && state.startsWith('apps_script_flow')) {
            const parts = state.split('|');
            if (parts.length > 1) {
              try {
                // Add padding back if needed before atob
                let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
                while (base64.length % 4) base64 += '=';
                const decodedUri = atob(base64);
                appsScriptRedirectUri = decodedUri;
                console.log('Decoded redirect URI from state:', decodedUri);
              } catch (e) {
                console.error('Failed to decode redirect URI from state:', e);
              }
            }
          }
        }

        if (error) {
          // When opened in a popup (e.g. Configure email → Gmail), close and notify parent so user stays on modal
          if (window.opener) {
            if (!cancelledNotifySentRef.current) {
              cancelledNotifySentRef.current = true;
              window.opener.postMessage(
                {
                  type: 'OAUTH_CANCELLED',
                  error,
                  errorDescription: searchParams.get('error_description') || undefined,
                  ...(isGoogleChatInboxFlow ? { provider: 'google_chat_inbox' } : {}),
                  ...(emailReauthAddress ? { emailReauthAddress } : {}),
                },
                window.location.origin
              );
            }
            if (!isAppsScriptFlow) {
              window.close();
              return;
            }
          }
          setError(`OAuth error: ${error}`);
          setLoading(false);
          return;
        }

        if (!code) {
          setError('No authorization code received');
          setLoading(false);
          return;
        }

        // Construct redirectUri based on current origin
        const redirectUri = `${window.location.origin}/auth/google/callback`;

        // Call the server endpoint to exchange code for tokens
        const response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/google/generateToken`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getNodeApiHeaders(),
          },
          body: JSON.stringify({ code, redirectUri }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const tokenResponse: TokenResponse = await response.json();
        setTokenData(tokenResponse);

        // Process Apps Script token delivery. Awaited so we only report success (and let
        // the user close the window) once Apps Script has actually confirmed receipt —
        // a fire-and-forget hidden-iframe form submit can be aborted mid-flight by
        // window.close(), silently dropping the tokens while the UI still says "Success".
        if (appsScriptRedirectUri) {
          console.log('Delivering tokens to Apps Script via fetch:', appsScriptRedirectUri);
          try {
            const payload = {
              access_token: tokenResponse.access_token || '',
              refresh_token: tokenResponse.refresh_token || '',
              expires_in: String(tokenResponse.expires_in || ''),
              token_type: tokenResponse.token_type || '',
              scope: tokenResponse.scope || '',
              email_id: tokenResponse.email_id || '',
            };

            const deliveryResponse = await fetch(appsScriptRedirectUri, {
              method: 'POST',
              headers: {
                'Content-Type': 'text/plain',
              },
              body: JSON.stringify(payload),
            });

            if (!deliveryResponse.ok) {
              throw new Error(`Apps Script responded with status ${deliveryResponse.status}`);
            }

            console.log('Tokens delivered to Apps Script successfully via fetch');
          } catch (postErr) {
            console.error('Error delivering tokens to Apps Script:', postErr);
            sessionStorage.removeItem('apps_script_redirect_uri');
            setError('Failed to send authentication tokens to the Gmail add-on. Please try again.');
            setLoading(false);
            return;
          }
          sessionStorage.removeItem('apps_script_redirect_uri');
        }

        setLoading(false);

        // Send data back to parent window and close if it's a popup
        if (window.opener) {
          window.opener.postMessage({
            type: 'OAUTH_SUCCESS',
            data: {
              provider: isGoogleChatInboxFlow ? 'google_chat_inbox' : 'gmail',
              ...tokenResponse,
              // The 'user@example.com' placeholder only makes sense for the Gmail flow (where it's
              // effectively dead code — Google's userinfo always returns a real email). Must NOT
              // apply to google_chat_inbox: AddSpaceModal would treat that placeholder as the real
              // connected mailbox and proceed as if a real account were connected.
              email_id: tokenResponse.email_id || (isGoogleChatInboxFlow ? '' : 'user@example.com'),
              ...(emailReauthAddress ? { emailReauthAddress } : {}),
            }
          }, window.location.origin);

          if (!isAppsScriptFlow) {
            window.close();
          }
        }
      } catch (err) {
        console.error('Error processing OAuth callback:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };

    handleOAuthCallback();
  }, [searchParams]);

  const handleGoBack = () => {
    navigate('/supportSpace');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          {isAppsScriptFlow ? 'Completing Apps Script Sync...' :
           isGoogleChatInboxFlow ? 'Authenticating to Google Chat...' : 'Authenticating to Gmail...'}
        </Typography>
      </Box>
    );
  }

  // If we have a parent window, don't show the success screen - just close
  if (window.opener && !error && !isAppsScriptFlow) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%',
          textAlign: 'center',
        }}
      >
        {error ? (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={handleGoBack}>
              Go Back
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h4" gutterBottom color="success.main">
              ✅ {isAppsScriptFlow ? 'Apps Script Sync Successful!' :
                  isGoogleChatInboxFlow ? 'Google Chat Authentication Successful!' : 'Gmail Authentication Successful!'}
            </Typography>

            <Typography variant="body1" sx={{ mb: 3 }}>
              {isAppsScriptFlow
                ? 'Your tokens have been securely sent back to Google Apps Script. You can now close this window.'
                : isGoogleChatInboxFlow
                ? 'Your Google Chat inbox has been successfully authenticated.'
                : 'Your Gmail account has been successfully authenticated.'}
            </Typography>

            {!isAppsScriptFlow && (
              <Button variant="contained" onClick={handleGoBack}>
                Continue to Support Spaces
              </Button>
            )}
            {isAppsScriptFlow && (
              <Button variant="outlined" onClick={() => window.close()}>
                Close Window
              </Button>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default GoogleOAuthCallback;
